import * as CL from '@design-system/component-library';
import { type JSX, useState } from 'react';
import { continueMsg, otherMsg, t } from '../../common/i18n';
import { ButtonGroupForSubmitAndBack } from '../ButtonGroupForSubmitAndBack/ButtonGroupForSubmitAndBack.js';
import { pictureStorageBaseUrl } from '../Picture/contentfulImageProxy';
import { EmployeeSupportOrServiceHeader } from './EmployeeSupportOrServiceHeader.js';
import { SupportOrServiceConfirmationDialog } from './SupportOrServiceConfirmationDialog.js';

import './EppSupportOrServiceAnnouncement.scss';

export interface EppSupportOrServiceAnnouncementProps {
  onCancel: () => void;
  onNext: () => void;
  isEmployee: boolean;
  onChangeDamageDescriptionType: (type: DamageDescriptionTypes | undefined) => void;
}

export enum DamageDescriptionTypes {
  SCREEN_BROKEN = 'SCREEN_BROKEN',
  CAMERA_ISSUE = 'CAMERA_ISSUE',
  CHARGING_ISSUE = 'CHARGING_ISSUE',
  HEADPHONES_ISSUE = 'HEADPHONES_ISSUE',
  BATTERY_ISSUE = 'BATTERY_ISSUE',
  OTHER_ISSUE = 'OTHER_ISSUE',
}

const AnnouncementTexts = ({
  onChangeDamageDescriptionType,
  damageDescriptionType,
}: {
  onChangeDamageDescriptionType: (type: DamageDescriptionTypes | undefined) => void;
  damageDescriptionType: DamageDescriptionTypes | undefined;
}): JSX.Element => (
  <>
    <div>
      {t.JWWE(
        'If your device is damaged, we will supply a replacement device. You will receive instructions for returning the old device along with the replacement.'
      )}
    </div>
    <div className="of-epp-maintenance__service-announcement-options">
      <CL.Dropdown
        key="damage-description"
        label={t.DNM5('Fault description')}
        i18n_dropdown_placeholderText={t.QRYV('Choose')}
        aria-label={t.DNM5('Fault description')}
        items={[
          { label: t.LLYE('Broken screen and/or back glass'), value: DamageDescriptionTypes.SCREEN_BROKEN },
          { label: t.ASOX('Camera problem'), value: DamageDescriptionTypes.CAMERA_ISSUE },
          { label: t.DOV9('Charging problem'), value: DamageDescriptionTypes.CHARGING_ISSUE },
          { label: t.Z9X2('Earpiece/microphone problem'), value: DamageDescriptionTypes.HEADPHONES_ISSUE },
          { label: t.XCJ3('Battery'), value: DamageDescriptionTypes.BATTERY_ISSUE },
          { label: t.C6WY(otherMsg), value: DamageDescriptionTypes.OTHER_ISSUE },
        ]}
        className="of-epp-maintenance__service-announcement-dropdown"
        integrated={false}
        onValueChange={value => {
          onChangeDamageDescriptionType(value?.dataset?.value as DamageDescriptionTypes | undefined);
        }}
      />
      <span className="of-epp-maintenance__service-announcement-footer">
        {t.GPJ3('Select the option that best describes the fault in your device.')}
      </span>
    </div>
    <div>
      {damageDescriptionType !== undefined &&
        damageDescriptionType !== DamageDescriptionTypes.BATTERY_ISSUE &&
        damageDescriptionType !== DamageDescriptionTypes.OTHER_ISSUE && (
          <p className="of-epp-maintenance__service-announcement-header">
            {t.JCT8('Before filing a service request, check and do the following:')}
          </p>
        )}
      {damageDescriptionType === DamageDescriptionTypes.SCREEN_BROKEN && (
        <div className="of-epp-maintenance__service-announcement-instructions-box">
          <ul>
            <li>
              {t.J9LT(
                'First, remove any screen protector and/or protective film that may be on the device’s display. Then check if the screen underneath is also damaged or if the touch issues persist.'
              )}
            </li>
          </ul>
        </div>
      )}
      {damageDescriptionType === DamageDescriptionTypes.CAMERA_ISSUE && (
        <div className="of-epp-maintenance__service-announcement-instructions-box">
          <ul>
            <li>
              {t.N1J1(
                'First, remove any protective glass that may have been placed over the camera lenses. Then check if the lens underneath is also damaged or if the camera issues persist.'
              )}
            </li>
          </ul>
        </div>
      )}
      {damageDescriptionType === DamageDescriptionTypes.CHARGING_ISSUE && (
        <div className="of-epp-maintenance__service-announcement-instructions-box">
          <ul>
            <li>
              {t.TLT9(
                'Dirt can accumulate at the bottom of the charging port, which may prevent charging from working. First, clean the charging port using, for example, a pin, safety pin, or fine-tipped tweezers (the charging port can withstand this type of handling).'
              )}
            </li>
            <li>{t.L11M('Check that the cable and power source are working properly.')}</li>
            <li>
              {t.NC66(
                'Charge the device with a working charger for at least 30 minutes, then try turning it on again. Sometimes the battery level gets so low that charging starts with a delay.'
              )}
            </li>
          </ul>
        </div>
      )}
      {damageDescriptionType === DamageDescriptionTypes.HEADPHONES_ISSUE && (
        <div className="of-epp-maintenance__service-announcement-instructions-box">
          <ul>
            <li>
              {t.PSP0(
                'Dust or dirt can accumulate over time in the earpiece or microphone opening, which may reduce sound quality or make the audio muffled. Before submitting a report, please make sure the opening is clean. You can clean it using, for example, a dry toothbrush.'
              )}
            </li>
          </ul>
        </div>
      )}
    </div>
  </>
);

export const EppSupportOrServiceAnnouncement = ({
  isEmployee,
  onNext,
  onCancel,
  onChangeDamageDescriptionType,
}: EppSupportOrServiceAnnouncementProps): JSX.Element => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [damageDescriptionType, setDamageDescriptionType] = useState<DamageDescriptionTypes | undefined>(undefined);

  return (
    <div className="of-epp-maintenance__service-announcement">
      {isEmployee && <EmployeeSupportOrServiceHeader />}
      <AnnouncementTexts
        onChangeDamageDescriptionType={(damageType: DamageDescriptionTypes | undefined) => {
          setDamageDescriptionType(damageType);
          onChangeDamageDescriptionType(damageType);
        }}
        damageDescriptionType={damageDescriptionType}
      />
      <hr />
      <ButtonGroupForSubmitAndBack
        className="of-epp-maintenance__service-announcement-buttons"
        onSubmit={() => {
          if (
            damageDescriptionType !== DamageDescriptionTypes.BATTERY_ISSUE &&
            damageDescriptionType !== DamageDescriptionTypes.OTHER_ISSUE
          ) {
            setShowConfirmDialog(true);
          } else {
            onNext();
          }
        }}
        onCancel={onCancel}
        submitButtonText={t.I62A(continueMsg)}
        submitDisabled={!damageDescriptionType}
      />
      {showConfirmDialog && (
        <SupportOrServiceConfirmationDialog
          icon={<img alt="Icon" src={`${pictureStorageBaseUrl}18h18UrHGnKJSZVn157DCk/SelfService.png`} width="100" />}
          header={t.GR5R('Have you checked and performed the instructed actions?')}
          body={t.OESQ(
            'We will have to cancel the change of device, if we find that the fault can be fixed with the instructed actions.'
          )}
          onCancel={() => setShowConfirmDialog(false)}
          onConfirm={onNext}
        />
      )}
    </div>
  );
};
